/**
 * What to refresh after a write, one function per family.
 *
 * Mutations used to enumerate keys in their onSuccess — the same four
 * membership keys in three places, the same seven booking keys in two — and
 * the enumerations drifted: one site would learn about a new key, the others
 * wouldn't, and the screen fed by the missing key showed stale data with no
 * error. The keys are hierarchical now (src/constants/query-keys.ts), so most
 * families are one prefix; these helpers are the single place that says which
 * prefixes a given kind of write touches.
 *
 * Each returns the combined invalidation promise so callers that need the
 * refetch to land first (e.g. before closing a modal) can await it; most
 * callers fire and forget, as onSuccess handlers always have.
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  CHECK_IN_QUERY_KEYS,
  CLASS_BOOKING_QUERY_KEYS,
  CLASS_QUERY_KEYS,
  DASHBOARD_QUERY_KEYS,
  GIFT_CARD_QUERY_KEYS,
  LOCKER_QUERY_KEYS,
  MEMBERSHIP_QUERY_KEYS,
  MEMBER_PHOTO_QUERY_KEYS,
  MEMBER_QUERY_KEYS,
  PT_SESSION_PACK_QUERY_KEYS,
  PT_SESSION_QUERY_KEYS,
  REPORT_QUERY_KEYS,
} from '@/constants/query-keys';

type Key = readonly unknown[];

function invalidateAll(queryClient: QueryClient, keys: readonly Key[]): Promise<void> {
  return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))).then(
    () => undefined,
  );
}

/**
 * Any membership write: lists, per-member and bulk lookups, every detail and
 * everything nested under one (freezes, notes, logs, refunds, transfers,
 * receipts, freeze summaries).
 */
export function invalidateMembershipFamily(queryClient: QueryClient): Promise<void> {
  return invalidateAll(queryClient, [MEMBERSHIP_QUERY_KEYS.ALL]);
}

/**
 * A class changed, or something counted on it did. The list carries the
 * derived booked/waitlist counts, so it always goes; with a class id the
 * class's whole scope goes too (record, roster, waitlist, logs, history).
 */
export function invalidateClassFamily(queryClient: QueryClient, classId?: string): Promise<void> {
  const keys: Key[] = [CLASS_QUERY_KEYS.ALL];
  if (classId) keys.push(CLASS_QUERY_KEYS.SCOPE(classId));
  return invalidateAll(queryClient, keys);
}

/**
 * A member took, lost or changed a place in a class (booking or waitlist).
 * Besides the class itself this changes the member's bookings tab and whether
 * they clash with any OTHER class — hence the whole conflict family.
 */
export function invalidateBookingFamily(
  queryClient: QueryClient,
  { classId, memberId }: { classId: string; memberId?: string | null },
): Promise<void> {
  const keys: Key[] = [
    CLASS_QUERY_KEYS.ALL,
    CLASS_QUERY_KEYS.SCOPE(classId),
    CLASS_BOOKING_QUERY_KEYS.CONFLICTS_ALL,
  ];
  if (memberId) keys.push(CLASS_BOOKING_QUERY_KEYS.BY_MEMBER(memberId));
  return invalidateAll(queryClient, keys);
}

/** A gift card was issued, edited, voided or redeemed. */
export function invalidateGiftCardFamily(queryClient: QueryClient): Promise<void> {
  return invalidateAll(queryClient, [GIFT_CARD_QUERY_KEYS.ALL]);
}

/**
 * A check-in was written or removed. The dashboard counts today's check-ins
 * under its own key, outside CHECK_IN_QUERY_KEYS.ALL; the check-in modal is
 * hosted in Layout, so this fires while the admin may be looking at that tile.
 */
export function invalidateCheckInFamily(queryClient: QueryClient): Promise<void> {
  return invalidateAll(queryClient, [
    CHECK_IN_QUERY_KEYS.ALL,
    DASHBOARD_QUERY_KEYS.TODAY_CHECK_INS_COUNT,
  ]);
}

/** A PT session or pack changed: every PT list plus the member's PT tabs. */
export function invalidatePTFamily(
  queryClient: QueryClient,
  memberId?: string | null,
): Promise<void> {
  const keys: Key[] = [PT_SESSION_QUERY_KEYS.ALL, PT_SESSION_PACK_QUERY_KEYS.ALL];
  if (memberId) {
    keys.push(PT_SESSION_QUERY_KEYS.BY_MEMBER(memberId), PT_SESSION_PACK_QUERY_KEYS.BY_MEMBER(memberId));
  }
  return invalidateAll(queryClient, keys);
}

/**
 * A locker key was given or returned. Also "In the House": a key out keeps the
 * member counted and its return is their check-out (members_in_house_now).
 */
export function invalidateLockerFamily(
  queryClient: QueryClient,
  memberId?: string | null,
): Promise<void> {
  const keys: Key[] = [LOCKER_QUERY_KEYS.ASSIGNMENTS_ALL, CHECK_IN_QUERY_KEYS.IN_THE_HOUSE];
  if (memberId) keys.push(LOCKER_QUERY_KEYS.BY_MEMBER(memberId));
  return invalidateAll(queryClient, keys);
}

/**
 * Every photo query that can show this member. The bulk lookups are keyed by
 * id lists, so they go wholesale.
 */
export function invalidateMemberPhotos(queryClient: QueryClient, memberId: string): Promise<void> {
  return invalidateAll(queryClient, [
    MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(memberId),
    MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(memberId),
    MEMBER_PHOTO_QUERY_KEYS.UPLOADED_BY_MEMBER(memberId),
    MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_ALL,
    MEMBER_PHOTO_QUERY_KEYS.ALL_PROFILE_BY_MEMBER(memberId),
    MEMBER_PHOTO_QUERY_KEYS.BULK_PROFILE_ALL,
  ]);
}

/**
 * Everything the Member Profile screen shows for one member, plus the
 * cross-member lists and counters it displays. Call after any write made
 * while viewing a member, so the tab or modal mounted next (e.g. the Check-In
 * modal opened right after adding a membership) reads fresh data rather than
 * the snapshot from before.
 */
export function invalidateMemberProfile(queryClient: QueryClient, memberId: string): Promise<void> {
  if (!memberId) return Promise.resolve();
  return Promise.all([
    // Record, privacy, notes, check-in history, logs, files, class bookings,
    // PT sessions and packs, lockers, friend passes, resource bookings…
    invalidateAll(queryClient, [MEMBER_QUERY_KEYS.SCOPE(memberId)]),
    invalidateMembershipFamily(queryClient),
    invalidateMemberPhotos(queryClient, memberId),
    invalidateAll(queryClient, [
      CHECK_IN_QUERY_KEYS.ALL,
      MEMBERSHIP_QUERY_KEYS.ACTIVE_MEMBER_GROWTH,
      REPORT_QUERY_KEYS.MRR,
    ]),
  ]).then(() => undefined);
}
